import { GroupStatusEvent, useIM } from '@wui/im'
import { computed, ComputedRef, MaybeRefOrGetter, onUnmounted, ref, toValue, watch } from 'vue'

import { conversionFromNumberToTime } from '@/shared/utils/date-time'

const recordingStartedAtByRoom = new Map<string, number>()

let isConferenceRecordingTimerInitialized = false

export const useConferenceRecordingTimerState = () => {
  const getRecordingStartedAt = (roomKey: string) => recordingStartedAtByRoom.get(roomKey)
  const clearRecordingStartedAt = (roomKey: string) => {
    recordingStartedAtByRoom.delete(roomKey)
  }
  const reset = () => {
    recordingStartedAtByRoom.clear()
  }

  return {
    getRecordingStartedAt,
    clearRecordingStartedAt,
    reset,
  }
}

export const syncConferenceRecordingStartedAt = (
  roomKey: string,
  isRecording: boolean,
) => {
  if (!roomKey) {
    return
  }

  if (!isRecording) {
    recordingStartedAtByRoom.delete(roomKey)
    return
  }

  // Не перезаписываем startedAt при уходе/возврате в звонок с активной записью
  if (!recordingStartedAtByRoom.has(roomKey)) {
    recordingStartedAtByRoom.set(roomKey, Date.now())
  }
}

const handleConferenceUpdated = (event: GroupStatusEvent) => {
  const { pServed, extendedParams: { isRecording }} = event
  syncConferenceRecordingStartedAt(pServed, Boolean(isRecording))
}

export const initConferenceRecordingTimer = () => {
  if (isConferenceRecordingTimerInitialized) {
    return
  }

  isConferenceRecordingTimerInitialized = true
  useIM().onConferenceUpdated(handleConferenceUpdated)
}

export const useConferenceRecordingTimer = (
  roomPServed: MaybeRefOrGetter<string>,
): { duration: ComputedRef<string> } => {
  initConferenceRecordingTimer()

  const tick = ref(0)
  let intervalId: ReturnType<typeof setInterval> | undefined

  const stopInterval = () => {
    if (!intervalId) {
      return
    }

    clearInterval(intervalId)
    intervalId = undefined
  }

  const startInterval = () => {
    if (intervalId) {
      return
    }

    intervalId = setInterval(() => {
      tick.value += 1
    }, 1000)
  }

  const duration = computed(() => {
    void tick.value

    const roomKey = toValue(roomPServed)
    const startedAt = recordingStartedAtByRoom.get(roomKey)

    if (!startedAt) {
      return conversionFromNumberToTime(0)
    }

    return conversionFromNumberToTime(Date.now() - startedAt)
  })

  watch(() => toValue(roomPServed), () => {
    startInterval()
  }, { immediate: true })

  onUnmounted(() => {
    stopInterval()
  })

  return {
    duration,
  }
}
